import Axios, { type AxiosRequestConfig } from 'axios';
import { useRuntimeConfig, useToken } from '#imports';
import { CancelablePromise } from '@/api/core/cancelable-promise';
import { requireOrganizationValue } from '@/composables/use-organization';
import { withOrganizationHeader } from '@/api/mutators/organization-header';
import { transformAxiosError } from '@/api/mutators/transform-axios-error';
import { ApiOrganizationNotSelectedError } from '@/errors';

const AXIOS_INSTANCE = Axios.create({
  baseURL: useRuntimeConfig().public.datastoreApiUrl,
});

export default <T>(config: AxiosRequestConfig): CancelablePromise<T> => {
  return new CancelablePromise(async (resolve, reject, onCancel) => {
    try {
      const source = Axios.CancelToken.source();

      // キャンセル処理を登録
      onCancel(() => {
        source.cancel('Request was cancelled');
      });

      // Auth0からBearerトークンを取得
      const bearerToken = await useToken();
      const organizationId = await requireOrganizationValue();

      const response = await AXIOS_INSTANCE({
        ...config,
        headers: withOrganizationHeader(
          {
            'Content-Type': 'application/json',
            'x-client-id': useRuntimeConfig().public.xClientId,
            'x-solution-id': useRuntimeConfig().public.xSolutionId,
            Authorization: `Bearer ${bearerToken}`,
            // trace 伝播は OTel auto-instrumentation が traceparent を自動付与する (FCDS-5453)
            // 呼び出し側が他のヘッダを上書きできるよう展開
            ...config.headers,
          },
          organizationId,
        ),
        cancelToken: source.token,
      });

      if (!onCancel.isCancelled) {
        resolve(response.data);
      }
    } catch (error) {
      if (!onCancel.isCancelled) {
        reject(error instanceof ApiOrganizationNotSelectedError ? error : transformAxiosError(error));
      }
    }
  });
};
